import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MEDIATYPES } from './constants';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { getTranslation } from 'common/react/utils/translations';

const DOM_READY_TIMEOUT = 1000;

export const MediaMiniPlayer = ({
    featureChannel,
    brightcoveProps,
    videoPlayerEleId,
    config = {},
    title,
    mediaType = MEDIATYPES.VIDEO,
    playsInline = false,
    onStreamEnded
}) => {
    const [hasInitialised, setHasInitialised] = useState(false);

    const playerWidgetRef = useRef(null);
    const playerInstanceRef = useRef(null);
    const { accountId, policyKey, playerId } = brightcoveProps;

    const brightcoveId = featureChannel.customAttributes.find(
        (attr) => attr.name === 'brightcove_videoid_web'
    )?.value;

    const mediaImageUrl = featureChannel.customAttributes.find(
        (attr) => attr.name === 'thumbnailURL'
    )?.value;

    const customAdTagUrl =
        mediaType === MEDIATYPES.AUDIO
            ? config?.audioAdTagUrl
            : config?.videoAdTagUrl;

    const disableAds = featureChannel.customAttributes.find(
        (attr) => attr.name === 'disableAds'
    )?.value;

    useEffect(() => {
        if (playerWidgetRef.current && !hasInitialised) {
            const additionalProperties = {
                popoutPlayerModel: {
                    mediaImageUrl,
                    title,
                    ...config,
                    featureChannel
                },
                playsinline: playsInline || mediaType === MEDIATYPES.AUDIO
            };

            if (customAdTagUrl) {
                additionalProperties.adTagUrl = customAdTagUrl;
            }

            if (disableAds) {
                additionalProperties.disableAds = disableAds;
            }

            if (mediaType === MEDIATYPES.AUDIO) {
                additionalProperties.isTradeRadio = true;
            }

            let player;
            if (mediaType === MEDIATYPES.AUDIO) {
                player = new PULSE.app.common.AudioPlayer(
                    playerWidgetRef.current,
                    [PULSE.app.common.middlewares.tradeRadioVideoPlay],
                    additionalProperties
                );
            } else {
                player = new PULSE.app.common.Video(
                    playerWidgetRef.current,
                    [PULSE.app.common.middlewares.tradeRadioVideoPlay],
                    additionalProperties
                );
            }

            playerInstanceRef.current = player;

            const addEndedListener = () => {
                setTimeout(() => {
                    const videoElement =
                        playerWidgetRef.current?.querySelector('.video-js');
                    if (videoElement && videoElement.player) {
                        videoElement.player.on('ended', () => {
                            if (onStreamEnded) {
                                onStreamEnded();
                            }
                        });
                    }
                }, DOM_READY_TIMEOUT);
            };

            addEndedListener();
            setHasInitialised(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasInitialised]);

    // Cleanup player on unmount
    useEffect(() => {
        return () => {
            if (
                playerInstanceRef.current &&
                playerInstanceRef.current.dispose
            ) {
                playerInstanceRef.current.dispose();
            }
        };
    }, []);

    return (
        <div data-widget="video-player" ref={playerWidgetRef}>
            <div className="player-container">
                <button
                    className="inline-player__button js-video-play"
                    aria-label={getTranslation('label.button.playVideo')}
                >
                    <SvgIcon icon="video" className="icon" />
                </button>

                <video-js
                    id={videoPlayerEleId}
                    data-video-id={brightcoveId}
                    data-account={accountId}
                    data-policy-key={policyKey}
                    data-player={playerId}
                    class={`inline-player__video video-js vjs-16-9 js-video-player ${
                        mediaType === MEDIATYPES.AUDIO
                            ? 'inline-player--no-fullscreen inline-player--audio-only'
                            : ''
                    }`}
                    controls
                    poster={mediaImageUrl}
                    autoplay="any"
                    playsinline={playsInline || mediaType === MEDIATYPES.AUDIO}
                ></video-js>
                {mediaType === MEDIATYPES.AUDIO && (
                    <div className="audio-ad-overlay__wrapper js-audio-ad-overlay-wrapper u-hide">
                        <div className="audio-ad-overlay__extra-details-container">
                            <button
                                className="js-custom-play audio-ad-overlay__custom-control"
                                aria-label="Play/pause audio"
                            >
                                <SvgIcon
                                    icon="video-pause"
                                    className="icon js-pause"
                                />
                                <SvgIcon
                                    icon="video-play-small"
                                    className="icon js-play u-hide"
                                />
                            </button>
                            <button
                                className="js-custom-volume audio-ad-overlay__custom-control"
                                aria-label="Mute/unmute audio"
                            >
                                <SvgIcon
                                    icon="volume-mute"
                                    className="icon js-mute u-hide"
                                />
                                <SvgIcon
                                    icon="volume-up"
                                    className="icon js-unmute"
                                />
                            </button>
                            <span className="video-ad-overlay__time-remaining js-audio-ad-overlay-time-remaining"></span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

MediaMiniPlayer.propTypes = {
    featureChannel: PropTypes.object.isRequired,
    brightcoveProps: PropTypes.object.isRequired,
    videoPlayerEleId: PropTypes.string.isRequired,
    config: PropTypes.object,
    title: PropTypes.string.isRequired,
    mediaType: PropTypes.string,
    onStreamEnded: PropTypes.func,
    playsInline: PropTypes.bool
};
